import { Fragment, useRef, useState } from 'react';
import { WorkItem, BidFormPayload, SubmitBidTerms, type ImportedBidSheet } from '../lib/api';
import { useResizableColumns } from '../lib/useResizableColumns';
import { CountUp } from './ui/CountUp';

const BID_COL: Record<string, number> = { item: 280, qty: 64, unit: 56, price: 110, ref: 96, amount: 96, remarks: 130 };
const BID_COL_IDS = ['item', 'qty', 'unit', 'price', 'amount', 'remarks'];
// 参考价 column inserted right after the labor's own 单价 (改改 fb_eadc55f7 18:16) — only when set.
const BID_COL_IDS_REF = ['item', 'qty', 'unit', 'price', 'ref', 'amount', 'remarks'];
const rmbCell = (n: number | null | undefined) => (n != null ? `¥${Math.round(n).toLocaleString('zh-CN')}` : '—');

interface BidFormProps {
  workItems: WorkItem[];
  busy: boolean;
  submitLabel: string;
  onSubmit: (payload: BidFormPayload) => void;
  serverError?: string | null;
  fieldErrors?: Record<string, string>;
  prefill?: { line_items: { work_item_id: string | null; unit_price_rmb: number | null; remarks_md?: string | null; item_label?: string | null; qty?: number | null; unit?: string | null }[]; terms?: SubmitBidTerms } | null;
  roundNumber?: number;
  /** Offline pricing round-trip (改改 6/26): when provided, show Export/Import controls that fill the form. */
  exportUrl?: string;
  onImportSheet?: (file: File) => Promise<ImportedBidSheet>;
  /** F2 · round gating (江卫春 6/26): when true, the bid is locked (already submitted) — read-only, no submit. */
  readOnly?: boolean;
  /** F2 · the submitted round shown in the lock banner. */
  lockedRound?: number;
}

export function BidForm({ workItems, busy, submitLabel, onSubmit, serverError, fieldErrors, prefill, roundNumber, exportUrl, onImportSheet, readOnly = false, lockedRound }: BidFormProps) {
  // 改改: highlight the lines changed in the latest BOQ revision so the labor instantly sees the adjustment points.
  const changedRev = Math.max(0, ...workItems.map((w) => w.changed_in_revision ?? 0));
  // 改改 fb_eadc55f7 (18:16): show the company 参考价 beside the labor's own price so they revise toward it.
  // Only surface the column once at least one line carries a reference price (else it just clutters the grid).
  const showRef = workItems.some((w) => w.ref_unit_price_rmb != null);
  const colIds = showRef ? BID_COL_IDS_REF : BID_COL_IDS;
  // unit_price state keyed by work_item id — lazy-initialized from prefill (supplements have null id; skipped here)
  const [prices, setPrices] = useState<Record<string, string>>(() => {
    if (!prefill) return {};
    const init: Record<string, string> = {};
    for (const li of prefill.line_items) {
      if (li.work_item_id && li.unit_price_rmb != null) init[li.work_item_id] = String(li.unit_price_rmb);
    }
    return init;
  });
  const [remarks, setRemarks] = useState<Record<string, string>>(() => {
    if (!prefill) return {};
    const init: Record<string, string> = {};
    for (const li of prefill.line_items) {
      if (li.work_item_id && li.remarks_md) init[li.work_item_id] = li.remarks_md;
    }
    return init;
  });

  // 江卫春 (fb_24398fd5 / fb_a7df4132): a line is "resolved" when it's priced OR explicitly skipped (不报此项).
  // With 200+ BOQ lines, one blank price silently blocked submit and the bidder couldn't find it — so we let
  // them skip lines they won't quote, and surface a count + jump for any still-unresolved line.
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});
  const [highlight, setHighlight] = useState(false);
  const toggleSkip = (id: string) => {
    setSkipped((s) => ({ ...s, [id]: !s[id] }));
    setPrices((v) => { const n = { ...v }; delete n[id]; return n; }); // skipping clears any stale price
  };

  // F3 · 自行补充项 (改改): free-form lines the BOQ doesn't list. Seeded from prefill supplements (null id + item_label).
  const [supplements, setSupplements] = useState<{ item_label: string; unit: string; qty: string; price: string }[]>(() => {
    if (!prefill) return [];
    return prefill.line_items
      .filter((li) => li.work_item_id == null && li.item_label)
      .map((li) => ({ item_label: li.item_label!, unit: li.unit ?? '', qty: li.qty != null ? String(li.qty) : '', price: li.unit_price_rmb != null ? String(li.unit_price_rmb) : '' }));
  });

  // optional terms — lazy-initialized from prefill
  const [showTerms, setShowTerms] = useState(() => {
    if (!prefill?.terms) return false;
    const t = prefill.terms;
    return !!(t.tax_response_md || t.warranty_response_md || t.payment_terms_response_md || t.validity_until);
  });
  const [taxMd, setTaxMd] = useState(() => prefill?.terms?.tax_response_md ?? '');
  const [warrantyMd, setWarrantyMd] = useState(() => prefill?.terms?.warranty_response_md ?? '');
  const [paymentMd, setPaymentMd] = useState(() => prefill?.terms?.payment_terms_response_md ?? '');
  const [validUntil, setValidUntil] = useState(() => prefill?.terms?.validity_until ?? '');
  // 改改 6/26 · single 税率 (drives 含税合计) — seeded from the bid's recorded tax note / Excel import
  const [taxPct, setTaxPct] = useState(() => { const m = /(-?\d+(?:\.\d+)?)\s*%/.exec(prefill?.terms?.tax_response_md ?? ''); return m ? m[1] : ''; });

  // 改改 6/26 · drag-resizable columns (widen 项目 to read the full 特征/计量/界限/品牌)
  const rz = useResizableColumns((id) => BID_COL[id] ?? 100);

  // group by category_top → category_sub
  const groups: { top: string; subs: { sub: string; items: WorkItem[] }[] }[] = [];
  for (const wi of workItems) {
    const top = wi.category_top ?? '其他 / Other';
    const sub = wi.category_sub ?? '';
    let grpTop = groups.find((g) => g.top === top);
    if (!grpTop) { grpTop = { top, subs: [] }; groups.push(grpTop); }
    let grpSub = grpTop.subs.find((s) => s.sub === sub);
    if (!grpSub) { grpSub = { sub, items: [] }; grpTop.subs.push(grpSub); }
    grpSub.items.push(wi);
  }

  // offline pricing round-trip (改改 6/26)
  const importRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f || !onImportSheet) return;
    setImporting(true); setImportMsg(null);
    try {
      const r = await onImportSheet(f);
      setPrices((cur) => { const n = { ...cur }; for (const li of r.line_items) if (li.work_item_id) n[li.work_item_id] = String(li.unit_price_rmb); return n; });
      setRemarks((cur) => { const n = { ...cur }; for (const li of r.line_items) if (li.work_item_id && li.remarks_md) n[li.work_item_id] = li.remarks_md; return n; });
      // F3 · route imported 自行补充项 (null id + item_label) into the supplement rows
      const importedSupps = r.line_items.filter((li) => li.work_item_id == null && li.item_label)
        .map((li) => ({ item_label: li.item_label!, unit: li.unit ?? '', qty: li.qty != null ? String(li.qty) : '', price: String(li.unit_price_rmb) }));
      if (importedSupps.length) setSupplements((cur) => [...cur, ...importedSupps]);
      if (r.tax_pct != null) setTaxPct(String(r.tax_pct));   // the single 税率 → 含税合计 + recorded with the bid
      const realMatched = r.matched - (r.supplements ?? 0);
      setImportMsg(`✓ 已导入 ${realMatched} 项报价${r.supplements ? `＋${r.supplements} 补充项` : ''}${r.tax_pct != null ? `，税率 ${r.tax_pct}%` : ''}${r.unmatched ? `（${r.unmatched} 项未匹配，已忽略）` : ''}，请核对后提交。/ Imported ${realMatched}${r.supplements ? ` + ${r.supplements} supplements` : ''} — review & submit.`);
    } catch (err) { setImportMsg(`⚠ ${(err as any)?.body?.error ?? (err as any)?.message ?? '导入失败 / import failed'}`); }
    finally { setImporting(false); if (e.target) e.target.value = ''; }
  }

  const priceNum = (id: string) => {
    const v = parseFloat(prices[id] ?? '');
    return isNaN(v) ? null : v;
  };

  // a line still needs attention if it's neither priced nor explicitly skipped (不报此项).
  const unresolved = workItems.filter((wi) => !skipped[wi.id] && priceNum(wi.id) == null);
  const pricedCount = workItems.filter((wi) => !skipped[wi.id] && priceNum(wi.id) != null).length;

  // 改改 6/26 · summary totals: 不含税合计 = Σ 工程量×不含税单价 (含 F3 自行补充项); 含税合计 = ×(1+税率)
  const taxRate = (() => { const v = parseFloat(taxPct); return isNaN(v) ? 0 : v / 100; })();
  const suppTotalEx = supplements.reduce((s, r) => { const p = parseFloat(r.price); const q = parseFloat(r.qty); return s + ((isNaN(p) ? 0 : p) * (isNaN(q) ? 1 : q)); }, 0);
  const totalEx = workItems.reduce((s, wi) => s + ((priceNum(wi.id) ?? 0) * (wi.qty ?? 1)), 0) + suppTotalEx;
  const totalIn = totalEx * (1 + taxRate);
  // a supplement is submittable only with a name + a valid non-negative price; partial rows are flagged, not silently sent.
  const suppActive = supplements.filter((r) => r.item_label.trim());
  const suppReady = suppActive.filter((r) => { const p = parseFloat(r.price); return !isNaN(p) && p >= 0; });
  const suppIncomplete = suppActive.length > suppReady.length;
  const rmb0 = (n: number) => `¥${Math.round(n).toLocaleString('zh-CN')}`;
  // submit is allowed once every line is resolved AND there is at least one priced line or a ready supplement.
  const canSubmit = unresolved.length === 0 && (pricedCount > 0 || suppReady.length > 0);
  function jumpToFirstUnresolved() {
    setHighlight(true);
    const first = unresolved[0];
    if (!first) return;
    const el = document.getElementById(`bid-price-${first.id}`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    (el as HTMLInputElement | null)?.focus();
  }

  function handleSubmit() {
    const lineItems: BidFormPayload['line_items'] = workItems
      .filter((wi) => !skipped[wi.id] && priceNum(wi.id) != null)
      .map((wi) => ({
        work_item_id: wi.id,
        unit_price_rmb: priceNum(wi.id)!,
        ...(remarks[wi.id] ? { remarks_md: remarks[wi.id] } : {}),
      }));
    // F3 · append complete 自行补充项 lines (work_item_id null + item_label)
    for (const r of suppReady) {
      const q = parseFloat(r.qty);
      lineItems.push({ work_item_id: null, item_label: r.item_label.trim(), unit_price_rmb: parseFloat(r.price), qty: isNaN(q) ? 1 : q });
    }
    const terms: SubmitBidTerms = {};
    const taxNote = taxPct.trim() ? `税率 ${taxPct.trim()}%` : taxMd;   // the single 税率 wins; recorded for 含税合计
    if (taxNote) terms.tax_response_md = taxNote;
    if (warrantyMd) terms.warranty_response_md = warrantyMd;
    if (paymentMd) terms.payment_terms_response_md = paymentMd;
    if (validUntil) terms.validity_until = validUntil;
    onSubmit({ line_items: lineItems, ...(Object.keys(terms).length ? { terms } : {}) });
  }

  return (
    <div>
      {roundNumber != null && (
        <div style={{ marginBottom: 10 }}>
          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, background: 'var(--stripe)', border: '1px solid var(--rule)', fontSize: 12, fontWeight: 600 }}>
            第 {roundNumber} 轮 / Round {roundNumber}
          </span>
        </div>
      )}

      {readOnly && (
        <div className="card" style={{ marginBottom: 12, borderLeft: '3px solid var(--accent)', background: 'var(--stripe)', fontSize: 13 }}>
          🔒 第 {lockedRound ?? roundNumber} 轮已交卷，您先歇口气 —— WTL 审阅后会开启下一轮。 / Round {lockedRound ?? roundNumber} is in — take a breather while WTL reviews.
        </div>
      )}

      {serverError && (
        <div className="card" style={{ borderColor: 'var(--bad)', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, marginBottom: 12 }}>
          {serverError}
        </div>
      )}

      {(exportUrl || (onImportSheet && !readOnly)) && (
        <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <span className="small muted">离线报价 / Offline pricing：</span>
          {exportUrl && <button type="button" onClick={() => window.open(exportUrl, '_blank')}>⬇ 导出报价表 / Export</button>}
          {onImportSheet && !readOnly && <>
            <button type="button" disabled={importing} onClick={() => importRef.current?.click()}>⬆ 导入已填表 / Import filled</button>
            <input ref={importRef} type="file" accept=".xls,.xlsx" style={{ display: 'none' }} onChange={handleImport} />
          </>}
          {importing && <span className="small muted">导入中… / importing</span>}
          {importMsg && <span className="small" style={{ color: importMsg.startsWith('⚠') ? 'var(--bad)' : 'var(--good)' }}>{importMsg}</span>}
        </div>
      )}

      {rz.resized && <div className="row" style={{ marginBottom: 4 }}><span className="spacer" style={{ flex: 1 }} /><button type="button" className="small" onClick={rz.reset}>↺ 重置列宽 / Reset</button></div>}
      <div className="cmp-wrap bid-scroll" style={{ maxHeight: 420, overflow: 'auto', marginBottom: 12 }}>
        <table className="cmp rz bid-table" style={{ fontSize: 13, width: rz.totalWidth(colIds) }}>
          <colgroup>{colIds.map((id) => <col key={id} style={{ width: rz.wOf(id) }} />)}</colgroup>
          <thead>
            <tr>
              <th>项目 / Item{rz.handle('item')}</th>
              <th>数量 / Qty{rz.handle('qty')}</th>
              <th>单位 / Unit{rz.handle('unit')}</th>
              <th>单价 (¥) / Unit price *{rz.handle('price')}</th>
              {showRef && <th title="公司不含税参考价 / company tax-exclusive reference price">不含税参考价{rz.handle('ref')}</th>}
              <th>合计 / Amount{rz.handle('amount')}</th>
              <th>备注 / Remarks{rz.handle('remarks')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <Fragment key={`grp-${gi}`}>
                <tr className="grp-row">
                  <td colSpan={colIds.length} className="cell-item" style={{ background: 'var(--stripe)', fontWeight: 600, fontSize: 12 }}>{g.top}</td>
                </tr>
                {g.subs.map((s, si) => (
                  <Fragment key={`sub-${gi}-${si}`}>
                    {s.sub && (
                      <tr className="grp-row">
                        <td colSpan={colIds.length} className="cell-item" style={{ background: 'var(--stripe)', fontSize: 11.5, paddingLeft: 24, color: 'var(--muted)' }}>{s.sub}</td>
                      </tr>
                    )}
                    {s.items.map((wi) => {
                      const p = priceNum(wi.id);
                      const isSkipped = !!skipped[wi.id];
                      const amount = p != null ? p * (wi.qty ?? 1) : null;
                      const ferr = fieldErrors?.[wi.id];
                      const needs = !readOnly && !isSkipped && p == null;          // unresolved: neither priced nor skipped
                      const isChanged = changedRev > 0 && (wi.changed_in_revision ?? 0) === changedRev;  // 改改: changed this revision
                      const rowClass = ferr ? 'row-error' : isChanged ? 'row-changed' : highlight && needs ? 'row-needs row-pulse' : '';
                      return (
                        <tr key={wi.id} className={rowClass || undefined}>
                          <td className="cell-item" style={{ paddingLeft: 32, whiteSpace: 'normal' }}>
                            {wi.item_label}
                            {isChanged && <span className="pill small pill-warn" style={{ marginLeft: 6 }}>本轮调整 / changed</span>}
                            {isChanged && wi.revision_note && <div className="small" style={{ color: 'var(--accent)' }}>调整 / changed：{wi.revision_note}</div>}
                            {wi.scope_description_md && <div className="small muted">特征 / spec：{wi.scope_description_md}</div>}
                            {wi.measurement_rule && <div className="small muted">计量 / measure：{wi.measurement_rule}</div>}
                            {wi.scope_boundary_md && <div className="small muted">界限 / boundary：{wi.scope_boundary_md}</div>}
                            {wi.brand_requirement && <div className="small muted">品牌 / brand：{wi.brand_requirement}</div>}
                          </td>
                          <td className="num" data-label="数量 / Qty">{wi.qty ?? '—'}</td>
                          <td className="num" data-label="单位 / Unit">{wi.unit ?? '—'}</td>
                          <td data-label="单价 (¥) / Price" style={{ minWidth: 110 }}>
                            <input
                              id={`bid-price-${wi.id}`}
                              type="number"
                              min={0}
                              step="0.01"
                              placeholder="0.00"
                              value={prices[wi.id] ?? ''}
                              disabled={readOnly || isSkipped}
                              onChange={(e) => setPrices((v) => ({ ...v, [wi.id]: e.target.value }))}
                              style={{ width: '100%', borderColor: ferr || (highlight && needs) ? 'var(--bad)' : undefined }}
                            />
                            {!readOnly && (
                              <label className="small muted" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', marginTop: 2, cursor: 'pointer' }}>
                                <input type="checkbox" checked={isSkipped} onChange={() => toggleSkip(wi.id)} /> 不报此项 / skip
                              </label>
                            )}
                            {ferr && <div className="small" style={{ color: 'var(--bad)' }}>{ferr}</div>}
                          </td>
                          {showRef && (
                            <td className="num" data-label="不含税参考价 / Ref" style={{ color: 'var(--muted)' }}>
                              {wi.ref_unit_price_rmb != null ? (
                                <>
                                  <div title="公司参考价 / company reference">{rmbCell(wi.ref_unit_price_rmb)}</div>
                                  {p != null && wi.ref_unit_price_rmb > 0 && Math.abs(p - wi.ref_unit_price_rmb) > 0.005 && (
                                    <div className="small" style={{ color: p > wi.ref_unit_price_rmb ? 'var(--bad)' : 'var(--good)' }}>
                                      {p > wi.ref_unit_price_rmb ? '高' : '低'} {Math.round(Math.abs(p - wi.ref_unit_price_rmb) / wi.ref_unit_price_rmb * 100)}%
                                    </div>
                                  )}
                                </>
                              ) : '—'}
                            </td>
                          )}
                          <td className="num" data-label="合计 / Amount">{isSkipped ? <span className="muted">不报 / —</span> : (amount != null ? `¥${Math.round(amount).toLocaleString('zh-CN')}` : '—')}</td>
                          <td data-label="备注 / Remarks" style={{ minWidth: 120 }}>
                            <input
                              type="text"
                              placeholder="可选 / optional"
                              value={remarks[wi.id] ?? ''}
                              disabled={readOnly}
                              onChange={(e) => setRemarks((v) => ({ ...v, [wi.id]: e.target.value }))}
                              style={{ width: '100%' }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* F3 · 自行补充项 (改改): bidder-added free-form lines the BOQ doesn't list */}
      {(!readOnly || supplements.length > 0) && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="row" style={{ alignItems: 'center', marginBottom: supplements.length ? 6 : 0, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>自行补充项 / Self-added items</strong>
            <span className="small muted" style={{ marginLeft: 8 }}>清单未列、需另行报价的项目 / lines not in the BOQ</span>
            <span className="spacer" style={{ flex: 1 }} />
            {!readOnly && <button type="button" onClick={() => setSupplements((s) => [...s, { item_label: '', unit: '', qty: '', price: '' }])}>+ 补充项 / Add</button>}
          </div>
          {supplements.length > 0 && (
            <table className="cmp bid-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>项目名称 / Item</th><th>单位 / Unit</th><th>工程量 / Qty</th><th>单价 (¥) / Unit price</th><th>合计 / Amount</th>{!readOnly && <th></th>}
                </tr>
              </thead>
              <tbody>
                {supplements.map((r, i) => {
                  const p = parseFloat(r.price); const q = parseFloat(r.qty);
                  const amt = isNaN(p) ? null : p * (isNaN(q) ? 1 : q);
                  const setRow = (patch: Partial<typeof r>) => setSupplements((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                  return (
                    <tr key={i}>
                      <td className="cell-item"><input type="text" value={r.item_label} disabled={readOnly} placeholder="名称 / name" onChange={(e) => setRow({ item_label: e.target.value })} style={{ width: '100%', minWidth: 140 }} /></td>
                      <td data-label="单位 / Unit"><input type="text" value={r.unit} disabled={readOnly} placeholder="项 / unit" onChange={(e) => setRow({ unit: e.target.value })} style={{ width: 56 }} /></td>
                      <td data-label="工程量 / Qty"><input type="number" min={0} value={r.qty} disabled={readOnly} placeholder="1" onChange={(e) => setRow({ qty: e.target.value })} style={{ width: 70 }} /></td>
                      <td data-label="单价 (¥) / Price"><input type="number" min={0} step="0.01" value={r.price} disabled={readOnly} placeholder="0.00" onChange={(e) => setRow({ price: e.target.value })} style={{ width: 90 }} /></td>
                      <td className="num" data-label="合计 / Amount">{amt != null ? `¥${Math.round(amt).toLocaleString('zh-CN')}` : '—'}</td>
                      {!readOnly && <td><button type="button" title="删除 / remove" onClick={() => setSupplements((s) => s.filter((_, j) => j !== i))}>✕</button></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!readOnly && suppIncomplete && <div className="small" style={{ color: 'var(--warn)', marginTop: 4 }}>补充项需填写名称和单价才会提交。/ Supplements need a name + price to be included.</div>}
        </div>
      )}

      {/* 改改 6/26 · single 税率 + summary totals (不含税合计 / 含税合计) */}
      <div className="card bid-totals" style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
        <label className="row" style={{ gap: 6, alignItems: 'center' }}>
          <span>税率 / Tax rate (%)：</span>
          <input type="number" min={0} step="0.01" placeholder="如 9 / e.g. 9" value={taxPct} disabled={readOnly} onChange={(e) => setTaxPct(e.target.value)} style={{ width: 80 }} />
        </label>
        <span className="spacer" style={{ flex: 1 }} />
        <span>不含税合计 / Pre-tax：<strong className="num"><CountUp value={totalEx} format={rmb0} /></strong></span>
        {/* 江卫春 6/26 (issue fb_86bb6ea6): a blank 税率 made 含税 == 不含税 and read as a bug — show a prompt instead. */}
        <span>含税合计 / Incl-tax：<strong className="num">{taxPct.trim() === '' ? '—（请填写税率 / enter 税率）' : <CountUp value={totalIn} format={rmb0} />}</strong></span>
      </div>

      {/* Optional terms */}
      <div style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => setShowTerms((v) => !v)} style={{ fontSize: 12 }}>
          {showTerms ? '▲' : '▼'} 附加条款 / Optional terms
        </button>
        {/* fb_c32c312c (江卫春): terms read as "can't be entered" when the round is locked — say so explicitly. */}
        {readOnly && <span className="small muted" style={{ marginLeft: 8 }}>🔒 本轮已锁定，开启下一轮后可编辑 / locked this round — editable once the next round opens</span>}
        {showTerms && (
          <div className="card" style={{ marginTop: 8, display: 'grid', gap: 10 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>税务说明 / Tax response</label>
              <textarea rows={2} value={taxMd} disabled={readOnly} onChange={(e) => setTaxMd(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>质保说明 / Warranty response</label>
              <textarea rows={2} value={warrantyMd} disabled={readOnly} onChange={(e) => setWarrantyMd(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>付款条款 / Payment terms response</label>
              <textarea rows={2} value={paymentMd} disabled={readOnly} onChange={(e) => setPaymentMd(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>报价有效期 / Validity until</label>
              <input type="date" value={validUntil} disabled={readOnly} onChange={(e) => setValidUntil(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="row" style={{ marginTop: 4, alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          {unresolved.length > 0 ? (
            <>
              <span className="small" style={{ color: 'var(--bad)' }}>
                还差 {unresolved.length} 项没报价 —— 填上单价，或勾「不报此项」即可。 / {unresolved.length} line(s) to go — price them or tick "skip".
              </span>
              <button type="button" className="small" onClick={jumpToFirstUnresolved}>定位第一项 / Jump to first</button>
            </>
          ) : !canSubmit ? (
            <span className="small muted">请至少填写一项报价 / enter at least one price.</span>
          ) : (
            <span className="small" style={{ color: 'var(--good)' }}>✓ 全部就绪，可以提交了（已报 {pricedCount} 项{Object.values(skipped).filter(Boolean).length ? `，跳过 ${Object.values(skipped).filter(Boolean).length} 项` : ''}） / All set — ready to submit</span>
          )}
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn-primary" disabled={busy || !canSubmit} onClick={handleSubmit}>
            {busy ? '提交中… / Submitting…' : submitLabel}
          </button>
        </div>
      )}
    </div>
  );
}
